import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import apiService from '../../services/api';

const LocationBulkUpload = ({ onUploadSuccess, onCancel }) => {
  const [file, setFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadProgress, setUploadProgress] = useState({
    isUploading: false,
    currentBatch: 0,
    totalBatches: 0,
    uploadedCount: 0,
    duplicateCount: 0,
    progress: 0
  });

  // Sample template data for download
  const sampleData = [
    {
      'Sl No': 1,
      'Revenue Division': 'Palakonda',
      'Mandal': 'Bhamini',
      'Revenue Village Code': '580070',
      'Mandal wise Sl No': 1,
      'Revenue Village Name': 'PALAVALASA'
    },
    {
      'Sl No': 2,
      'Revenue Division': 'Palakonda',
      'Mandal': 'Bhamini',
      'Revenue Village Code': '580071',
      'Mandal wise Sl No': 2,
      'Revenue Village Name': 'MANUMAKONDA'
    }
  ];

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' && 
          selectedFile.type !== 'application/vnd.ms-excel') {
        setError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setError('');
      setSuccessMessage('');
    }
  };

  const parseExcelFile = () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    setLoading(true);
    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          setError('The Excel file is empty');
          setLoading(false);
          return;
        }

        // Validate required columns
        const requiredColumns = ['Revenue Division', 'Mandal', 'Revenue Village Name'];
        const firstRow = jsonData[0];
        const missingColumns = requiredColumns.filter(col => !(col in firstRow));

        if (missingColumns.length > 0) {
          setError(`Missing required columns: ${missingColumns.join(', ')}`);
          setLoading(false);
          return;
        }

        // Transform data to match our backend expectations
        const transformedData = jsonData.map((row, index) => ({
          slNo: row['Sl No'] || null,
          revenueDivision: row['Revenue Division']?.toString().trim() || '',
          mandal: row['Mandal']?.toString().trim() || '',
          revenueVillageCode: row['Revenue Village Code']?.toString().trim() || '',
          mandalWiseSlNo: row['Mandal wise Sl No'] || null,
          revenueVillageName: row['Revenue Village Name']?.toString().trim() || '',
          rowNumber: index + 1
        }));

        // Basic validation
        const validationErrors = [];
        transformedData.forEach(row => {
          if (!row.revenueVillageName) {
            validationErrors.push(`Row ${row.rowNumber}: Revenue Village Name is required`);
          }
          if (!row.revenueDivision) {
            validationErrors.push(`Row ${row.rowNumber}: Revenue Division is required`);
          }
          if (!row.mandal) {
            validationErrors.push(`Row ${row.rowNumber}: Mandal is required`);
          }
        });

        if (validationErrors.length > 0) {
          setError(`Validation errors:\n${validationErrors.slice(0, 10).join('\n')}${validationErrors.length > 10 ? '\n... and more' : ''}`);
          setLoading(false);
          return;
        }

        setPreviewData(transformedData);
        setIsPreviewMode(true);
        setLoading(false);
      } catch (err) {
        setError('Error parsing Excel file. Please check the file format.');
        setLoading(false);
      }
    };

    reader.readAsArrayBuffer(file);
  };

  const handleConfirmUpload = async () => {
    setLoading(true);
    setError('');

    try {
      // First, try regular bulk upload for smaller datasets
      if (previewData.length <= 500) {
        const result = await apiService.post('/locations/bulk-upload', { 
          locations: previewData 
        });

        if (result.status === 'batch_required') {
          // Server suggests batch processing
          await processBatchUpload();
        } else {
          setSuccessMessage(`Successfully uploaded ${result.data.uploadedCount} locations`);
          setTimeout(() => {
            onUploadSuccess && onUploadSuccess();
          }, 2000);
        }
      } else {
        // Use batch processing for large datasets
        await processBatchUpload();
      }
    } catch (err) {
      setError(err.message || 'Upload failed');
      if (err.errors) {
        setError(`${err.message}\n${err.errors.slice(0, 5).join('\n')}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const processBatchUpload = async () => {
    const BATCH_SIZE = 100;
    const totalBatches = Math.ceil(previewData.length / BATCH_SIZE);
    const batchId = Date.now().toString();
    
    setUploadProgress({
      isUploading: true,
      currentBatch: 0,
      totalBatches,
      uploadedCount: 0,
      duplicateCount: 0,
      progress: 0
    });

    let totalUploaded = 0;
    let totalDuplicates = 0;
    const failedBatches = [];

    try {
      for (let i = 0; i < totalBatches; i++) {
        const start = i * BATCH_SIZE;
        const end = start + BATCH_SIZE;
        const batch = previewData.slice(start, end);

        setUploadProgress(prev => ({
          ...prev,
          currentBatch: i + 1,
          progress: Math.round(((i + 1) / totalBatches) * 100)
        }));

        try {
          const result = await apiService.post('/locations/batch-upload', {
            batch,
            batchIndex: i,
            totalBatches,
            batchId
          });

          totalUploaded += result.data.uploadedCount;
          totalDuplicates += result.data.duplicateCount || 0;

          setUploadProgress(prev => ({
            ...prev,
            uploadedCount: totalUploaded,
            duplicateCount: totalDuplicates
          }));

          // Small delay to prevent overwhelming the server
          await new Promise(resolve => setTimeout(resolve, 100));

        } catch (batchError) {
          console.error(`Batch ${i + 1} failed:`, batchError);
          failedBatches.push(i + 1);
          
          // Continue with next batch instead of failing completely
          continue;
        }
      }

      // Upload completed
      let message = `Successfully uploaded ${totalUploaded} locations`;
      if (totalDuplicates > 0) {
        message += ` (${totalDuplicates} duplicates skipped)`;
      }
      if (failedBatches.length > 0) {
        message += `. Failed batches: ${failedBatches.join(', ')}`;
      }

      setSuccessMessage(message);
      setUploadProgress(prev => ({ ...prev, isUploading: false }));
      
      setTimeout(() => {
        onUploadSuccess && onUploadSuccess();
      }, 3000);

    } catch (error) {
      setError(`Batch upload failed: ${error.message}`);
      setUploadProgress(prev => ({ ...prev, isUploading: false }));
    }
  };

  const downloadTemplate = () => {
    const ws = XLSX.utils.json_to_sheet(sampleData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Locations Template');
    XLSX.writeFile(wb, 'locations_template.xlsx');
  };

  const resetForm = () => {
    setFile(null);
    setPreviewData([]);
    setIsPreviewMode(false);
    setError('');
    setSuccessMessage('');
    // Reset file input
    const fileInput = document.getElementById('excel-file-input');
    if (fileInput) fileInput.value = '';
  };

  if (successMessage && !uploadProgress.isUploading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <div className="mb-4 text-green-600">
              <svg className="w-16 h-16 mx-auto" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Upload Successful!</h3>
            <p className="text-gray-600 mb-4 whitespace-pre-line">{successMessage}</p>
            <button
              onClick={() => onUploadSuccess && onUploadSuccess()}
              className="w-full bg-green-600 text-white py-2 px-4 rounded-md hover:bg-green-700 transition-colors"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Progress indicator for batch upload
  if (uploadProgress.isUploading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Uploading Locations...</h3>
            
            {/* Progress Bar */}
            <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
              <div 
                className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                style={{ width: `${uploadProgress.progress}%` }}
              ></div>
            </div>
            
            {/* Progress Details */}
            <div className="space-y-2 text-sm text-gray-600">
              <p>Batch {uploadProgress.currentBatch} of {uploadProgress.totalBatches}</p>
              <p>Progress: {uploadProgress.progress}%</p>
              <p>Uploaded: {uploadProgress.uploadedCount} locations</p>
              {uploadProgress.duplicateCount > 0 && (
                <p>Duplicates skipped: {uploadProgress.duplicateCount}</p>
              )}
            </div>
            
            {/* Spinner */}
            <div className="mt-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            </div>
            
            <p className="text-xs text-gray-500 mt-4">
              Please don't close this window until upload is complete
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {isPreviewMode ? 'Preview Locations Data' : 'Bulk Upload Locations'}
          </h2>
          <button
            onClick={onCancel}
            className="text-gray-500 hover:text-gray-700"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {!isPreviewMode ? (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2">Instructions</h3>
              <ul className="text-blue-800 text-sm space-y-1">
                <li>• Download the template Excel file to see the required format</li>
                <li>• Required columns: Revenue Division, Mandal, Revenue Village Name</li>
                <li>• Optional columns: Sl No, Revenue Village Code, Mandal wise Sl No</li>
                <li>• Make sure all required fields are filled</li>
                <li>• Upload only .xlsx or .xls files</li>
                <li>• For large files (500+ rows), automatic batch processing will be used</li>
                <li>• Batch processing prevents server timeouts and provides progress tracking</li>
              </ul>
            </div>

            {/* Download Template */}
            <div className="flex justify-center">
              <button
                onClick={downloadTemplate}
                className="bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors flex items-center"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Template
              </button>
            </div>

            {/* File Upload */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
              <input
                id="excel-file-input"
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="hidden"
              />
              <label htmlFor="excel-file-input" className="cursor-pointer">
                <div className="text-gray-600">
                  <svg className="w-12 h-12 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium">Click to select Excel file</p>
                  <p className="text-sm text-gray-500">or drag and drop</p>
                </div>
              </label>
              {file && (
                <div className="mt-4 p-2 bg-gray-100 rounded">
                  <p className="text-sm text-gray-700">Selected: {file.name}</p>
                </div>
              )}
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={onCancel}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={parseExcelFile}
                disabled={!file || loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Processing...' : 'Preview Data'}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Preview Summary */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h3 className="font-semibold text-green-900 mb-2">Data Preview</h3>
              <p className="text-green-800 text-sm">
                Found {previewData.length} locations ready for upload. Please review and confirm.
              </p>
              {previewData.length > 500 && (
                <p className="text-yellow-700 text-sm mt-2 font-medium">
                  ⚡ Large dataset detected ({previewData.length} rows). 
                  Batch processing will be used to prevent timeouts.
                  Upload will be processed in {Math.ceil(previewData.length / 100)} batches of 100 rows each.
                </p>
              )}
            </div>

            {/* Preview Table */}
            <div className="overflow-x-auto max-h-96 border border-gray-200 rounded-lg">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sl No</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Division</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mandal</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Village Code</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mandal wise Sl No</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Revenue Village Name</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {previewData.map((location, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{location.slNo || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{location.revenueDivision}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{location.mandal}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{location.revenueVillageCode || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{location.mandalWiseSlNo || '-'}</td>
                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">{location.revenueVillageName}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-800 whitespace-pre-line">{error}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4">
              <button
                onClick={resetForm}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
              >
                Back to Upload
              </button>
              <button
                onClick={handleConfirmUpload}
                disabled={loading}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? 'Uploading...' : 'Confirm Upload'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LocationBulkUpload;